import { useSelector } from 'react-redux'
import { jt, t } from 'ttag'
import { Link } from 'react-router-dom'

import { useTitleChange } from '../../util/hooks'

const CollegeComparison = props => {
  useTitleChange(t`Educational Institution Comparison`)

  //useInitDispatch(compareInstitutions(props.location.query.institution), [props.location.query.institution])

  const { error, fluidContainer = false } = props
  const comparisons = useSelector(
    state => state.college.collegeComparison.result?.comparisons
  )

  if (!comparisons) {
    return null
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-warning" role="alert">
          <h3>{error}</h3>
        </div>
      </div>
    )
  }

  const boldStar = <em key="boldstar">*</em>
  const regionName = props.result?.defaultRegion?.name
  const satInRange = r => {
    if (r.satMathPercentile25 && r.satCriticalReadingPercentile25) {
      const min = (r?.satMathPercentile25 ?? 0) + (r?.satCriticalReadingPercentile25 ?? 0)
      const max = (r?.satMathPercentile75 ?? 0) + (r?.satCriticalReadingPercentile75 ?? 0)
      const userMin = props.result?.collegePrefs?.satMin
      const userMax = props.result?.collegePrefs?.satMax

      return userMin <= max && userMax >= min
    }

    return false
  }
  const actInRange = r => {
    if (r.actCompositePercentile25) {
      const min = r?.actCompositePercentile25 ?? 0
      const max = r?.actCompositePercentile75 ?? 0
      const userMin = props.result?.collegePrefs?.actMin
      const userMax = props.result?.collegePrefs?.actMax

      return userMin <= max && userMax >= min
    }

    return false
  }

  return (
    <div className={fluidContainer ? 'container-fluid' : 'container'}>
      <table id="comparison" className="tbl">
        <thead>
          <tr>
            <th className="comparison-heading">
              <span className="sr-only">{t`Comparison Attribute`}</span>
            </th>
            {(comparisons || []).map((coll, idx) => (
              <th className="occupation-title" key={`${coll?.url}-${idx}`} scope="col">
                <Link
                  to={coll?.url}
                  style={{ fontWeight: 'bold' }}
                  className="occupation-link regular-match">
                  {coll?.title}
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Location`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`location${idx}`} className="occupation-title">
                {data?.location}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Highest Offering`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`offering${idx}`} className="occupation-title">
                {data?.highestOffering}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Student Body Size`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`size${idx}`} className="occupation-title">
                {data?.size}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Campus Setting`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`setting${idx}`} className="occupation-title">
                {data?.setting}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Type`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`setting${idx}`} className="occupation-title">
                {data?.sector}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Tuition`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`setting${idx}`} className="occupation-title">
                {(data?.isInState
                  ? data?.costsInState
                  : data?.costsOutOfState
                )?.toLocaleString('en', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0
                }) ?? `n/a`}{' '}
                {data?.isInState && data?.costsInState !== data?.costsOutOfState
                  ? '*'
                  : ''}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Room & Board`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`setting${idx}`} className="occupation-title">
                {data?.costsRoomBoard?.toLocaleString('en', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0
                }) ?? `n/a`}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Average Aid **`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`setting${idx}`} className="occupation-title">
                {data?.aid?.toLocaleString('en', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0
                }) ?? `n/a`}
              </td>
            ))}{' '}
            <span className="unchecked">
              <span>{t`no`}</span>
            </span>
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Average SAT`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`sat${idx}`} className="occupation-title">
                {data.satMathPercentile25 && data.satCriticalReadingPercentile25
                  ? `${
                      (data?.satMathPercentile25 ?? 0) +
                      (data?.satCriticalReadingPercentile25 ?? 0)
                    } - 
                                      ${
                                        (data?.satMathPercentile75 ?? 0) +
                                        (data?.satCriticalReadingPercentile75 ?? 0)
                                      }`
                  : `n/a`}
                {satInRange(data) ? (
                  <div className="inassessment">{t`This is in your specified SAT range`}</div>
                ) : null}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Average ACT`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`act${idx}`} className="occupation-title">
                {data.actCompositePercentile25 && data.actCompositePercentile25
                  ? `${data?.actCompositePercentile25 ?? 0} - 
                                        ${data?.actCompositePercentile75 ?? 0}`
                  : `n/a`}
                {actInRange(data) ? (
                  <div className="inassessment">{t`This is in your specified ACT range`}</div>
                ) : null}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading">{t`Acceptance Rate`}</th>
            {(comparisons || []).map((data, idx) => {
              const totalAdmit = data?.totalAdmissions?.toLocaleString()
              const totalApply = data?.totalApplications?.toLocaleString()

              return (
                <td key={`admissions${idx}`} className="occupation-title">
                  {data.totalAcceptance ? (
                    <span>
                      {data.totalAcceptance}%{' '}
                      <span
                        style={{
                          fontSize: '.8rem'
                        }}>
                        {t`(${totalAdmit} of ${totalApply} applications)`}
                      </span>
                    </span>
                  ) : (
                    ``
                  )}
                </td>
              )
            })}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading">{t`Important Dates`}</th>
            {(comparisons || []).map((data, idx) => (
              <td key={`dates${idx}`} className="occupation-title">
                <b>{t`Application Due Date:`}</b>{' '}
                {data.openAdmissionsPolicy === 1 ? t`open admission` : t`not available`}
              </td>
            ))}
          </tr>
        </tbody>
        <tfoot>
          <tr className="even">
            <td></td>
            {(comparisons || []).map((data, idx) => (
              <td key={`link${idx}`} className="profile-link">
                <Link to={data?.url} className="submit">
                  {t`View College`}
                </Link>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
      <div className="disclaimers">
        <p>
          {jt`* - Tuition is displayed based on what a typical ${regionName} resident would pay. 
                        When an in-state tuition is displayed, it is further marked with an ${boldStar}. For complete information on pricing for
                        in-state, out-of-state, and in-district residents - please view the full profile.`}
          <br />
          {t`** - Financial aid is only available to those who qualify.`}
        </p>
      </div>
    </div>
  )
}

export { CollegeComparison }
